import React, { useCallback } from "react";
import {
    Card,
    CardActions,
    CardContent,
    Typography,
    Button,
} from "@mui/material/";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import actions from "../../Store/actions";
import makeStyles from "@mui/styles/makeStyles";
import EditIcon from "@mui/icons-material/Edit";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import styles from "./styles.js";
import useTypesListMake from "../../common/hooks/useTypesListMake.js";
import useTypesListAssetTypes from "../../common/hooks/useTypesListAssetTypes.js";

type Props = {
    itemContent: object,
    onDefer: Function,
    OnExclude: Function,
};

const AssetItem = ({
    assetId,
    assetType,
    itemContent,
    onDefer,
    onExclude,
}: Props) => {
    let navigate = useNavigate();
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const params = useParams();
    const { auditId } = params;

    let assetCore = {};

    if (assetType === "checked")
        assetCore = itemContent.assetCore ? itemContent.assetCore : itemContent;
    else if (assetType === "unchecked") assetCore = itemContent.assetCore;

    /** Check if altAsset */
    const isAltAsset = itemContent.createdBy ? true : false;

    let assetAttributes = itemContent.assetAttributes;

    const { make, type } = assetCore;
    const makeIdentifier = useTypesListMake(make);
    const typeIdentifier = useTypesListAssetTypes(type);

    const onCardClick = (assetId) => {
        // this dispatch will work only unchecked assets
        dispatch(actions.auditAssets.create.itemSet(itemContent));
        navigate(`/auth/audits/${auditId}/assets/${assetId}/details`);
    };

    const generateCardText = useCallback((primaryText, variantType) => {
        return (
            <Typography
                key={`${primaryText}`}
                gutterBottom
                variant={variantType === "body1" ? "body1" : "body2"}
                align="left"
            >
                {primaryText}
            </Typography>
        );
    }, []);

    const generateIconStatus = (status) => {
        if (status && status === 2)
            return <VisibilityIcon className={classes.nonClickableIcon} />;
        if (status && status === 3)
            return <VisibilityOffIcon className={classes.nonClickableIcon} />;
        if (!status || (status && status == 1))
            return <SyncAltIcon className={classes.nonClickableIcon} />;
    };

    const generateCardAction = (
        assetId,
        status,
        primaryBtnClick,
        PrimaryIcon,
        secondaryBtnClick,
        SecondaryIcon
    ) => {
        return (
            <CardActions className={classes.cardActions}>
                <Button
                    aria-label={`asset-primary-button-${assetId}`}
                    role="button"
                    className={`${classes.actionColButtons} ${classes.actionColFirstChild}`}
                    onClick={primaryBtnClick}
                >
                    {PrimaryIcon ? (
                        <PrimaryIcon
                            color={assetType === "unchecked" ? "error" : "info"}
                        />
                    ) : (
                        generateIconStatus(status)
                    )}
                </Button>
                <Button
                    aria-label={`asset-secondary-button-${assetId}`}
                    role="button"
                    className={`${classes.actionColButtons} ${classes.actionColFirstChild}`}
                    onClick={secondaryBtnClick}
                >
                    <SecondaryIcon color="info" />
                </Button>
            </CardActions>
        );
    };

    //  Filter function to exclude certain keys and non-string values
    // 1. for unexpected asset remove uuid createdDate
    // 2. For expected asset remove id
    // 3. Remove make and type in general

    const removeObjectEntries = (obj) => {
        return Object.entries(obj).filter(
            ([key, value]) =>
                key !== "id" &&
                key !== "make" &&
                key !== "type" &&
                key !== "createdDate" &&
                key !== "uuid" &&
                typeof value === "string" &&
                value
        );
    };
    const coreItems = removeObjectEntries(assetCore);

    // Desired keys in order
    const orderedKeys = ["identifier1", "identifier2"];

    // Separate ordered entries
    const primaryEntries = orderedKeys
        .map((key) => coreItems.find(([k]) => k === key))
        .filter(Boolean); // Ensure the entry exists

    // Remaining entries
    const remainingEntries = coreItems.filter(
        ([key]) => !orderedKeys.includes(key)
    );

    const hdlCardClick = useCallback(() => onCardClick(assetId), []);

    return (
        <Card raised={true} className={classes.cardContainer}>
            <CardContent
                className={classes.cardContent}
                onClick={!itemContent.createdBy ? hdlCardClick : null}
            >
                {primaryEntries.map(([key, value]) =>
                    generateCardText(value, "body1")
                )}
                {makeIdentifier.name && generateCardText(makeIdentifier.name)}
                {typeIdentifier.name && generateCardText(typeIdentifier.name)}
                {remainingEntries.map(([key, value]) =>
                    generateCardText(value)
                )}
            </CardContent>

            {/*  Unchecked asset */}
            {assetType === "unchecked" &&
                generateCardAction(
                    assetId,
                    itemContent.status,
                    onExclude,
                    RemoveCircleOutlineIcon,
                    onDefer,
                    AccessTimeIcon
                )}

            {/*  Checked asset */}
            {!isAltAsset &&
                assetType === "checked" &&
                generateCardAction(
                    assetId,
                    itemContent.status,
                    null,
                    null,
                    hdlCardClick,
                    EditIcon
                )}

            {/*  Alt asset */}
            {isAltAsset &&
                generateCardAction(
                    assetId,
                    itemContent.status,
                    null,
                    null,
                    null,
                    ErrorOutlineIcon
                )}
        </Card>
    );
};

export default AssetItem;
